/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { CompanyFilterMode, filtersEmptyValues, FiltersProps } from "@src/components/Header/Header.constants";
import { RootState } from "./store";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { InventoryDisplayMode } from "@src/utils/inventoryDisplayMode";
import { EventNoteDto } from "@src/types/notes";

interface Event {
	id: string;
	name: string;
	eventDateTime: string;
	marketplace: string;
	venue: {
		name: string;
		city: string;
		state: string;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;

	sfEventName: string | null;
	sfEventDate: string | null;
	sfEventVenue: string | null;
	sfEventCity: string | null;
	sfEventState: string | null;
	isParkingEvent?: boolean;
	isMultiDayEvent?: boolean;
	isPostponed?: boolean;
	isCancelled?: boolean;

	ticketQtySf?: number;
	ticketQtySelectedCompanies?: number;

	automapperResult?: {
		similarity: number;
		processedDate: string;
		matchingType: string;
		bestResults: Array<{
			id: string;
			similarity: number;
			name: string;
			date: string;
			venue: string;
			city: string;
			state: string;
		}>;
	};
	notes?: Array<EventNoteDto>;
}

interface EventsState {
	events: Array<Event>;
	totalEventsCount: number;
	loading: "idle" | "pending" | "succeeded" | "failed";
	loadingSuggestions: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
	filters: FiltersProps;
	tableType: TableType;
	currentPage: number;
	itemsPerPage: number;
	mappingSuggestions: Array<MappingSuggestion>;
	latestRequestId: number | null;
}

interface MappingSuggestion {
	eventId: string;
	mpEventId?: string | null;
	ticketmasterProductionEventId?: string | null;
	eventName: string;
	eventDate: string;
	eventTime: string;
	active: boolean;
	createdDate: string;
	updatedDate: string;
	venueName: string;
	cityName: string;
	stateName: string;
	headlinerName: string;
	marketplace: string;
	matchScore: number;
	mpEventStatus: string;
	matchDetails?: {
		eventNamePct?: number | null;
		venueNamePct?: number | null;
		cityPct?: number | null;
		statePct?: number | null;
		dateMatch?: boolean | null;
		timeMatch?: boolean | null;
	};
}

/** The shape of the entire response from the new endpoint */
interface MappingSuggestionsResponse {
	total_events_count: number;
	events: Array<MappingSuggestion>;
}

const initialState: EventsState = {
	events: [],
	totalEventsCount: 0,
	loading: "idle",
	loadingSuggestions: "idle",
	error: null,
	filters: filtersEmptyValues,
	tableType: "UNMAPPED_EVENTS",
	currentPage: 1,
	itemsPerPage: 30,
	mappingSuggestions: [],
	latestRequestId: null,
};


type TableType = "UNMAPPED_EVENTS" | "MAPPED_EVENTS" | "SLEPT_EVENTS";

export const fetchEvents = createAsyncThunk(
	"events/fetchEvents",
	async (
		{ inventoryDisplayMode, includeInactiveEvents, requestId }:
			{ inventoryDisplayMode?: InventoryDisplayMode; includeInactiveEvents?: boolean; requestId: number },
		{ getState, rejectWithValue },
	) => {
		try {
			const state = getState() as RootState;

			// 1) Read params from the URL
			const urlParams = new URLSearchParams(window.location.search);
			const marketplaceParam = urlParams.get("marketplace");
			const companyFilterModeParam = urlParams.get("companyFilterMode");
			const companyIdsParam = urlParams.get("companyIds");
			const sortByParam = urlParams.get("sortBy");
			const sortDirectionParam = urlParams.get("sortDirection");
			const searchValue = urlParams.get("search");
			const categoryIdsParam = urlParams.get("categoryId");
			const hasNotes = urlParams.get("hasNotes") === "true";
			const includeInactiveEventsParam = urlParams.get("includeInactiveEvents");
			const resolvedIncludeInactiveEvents =
				includeInactiveEvents !== undefined
					? includeInactiveEvents
					: includeInactiveEventsParam === null
						? undefined
						: includeInactiveEventsParam === "true";

			// If the URL doesn't have marketplace, fallback to Redux filter
			const marketplace = marketplaceParam ?? state.events.filters.marketplace;
			const categoryId =
				(categoryIdsParam?.split(",").map((x) => x.trim()).filter(Boolean)) ??
				(state.events.filters.categoryId ?? []);
			const companyFilterMode =
				companyFilterModeParam === CompanyFilterMode.ALL ||
				companyFilterModeParam === CompanyFilterMode.INCLUDE ||
				companyFilterModeParam === CompanyFilterMode.EXCLUDE
					? companyFilterModeParam
					: CompanyFilterMode.ALL;
			const companyIds = companyIdsParam?.split(",").map((x) => x.trim()).filter(Boolean) ?? [];
			const isCompanyFilterTable =
				state.events.tableType === "UNMAPPED_EVENTS" || state.events.tableType === "MAPPED_EVENTS";
			const isCategoryFilterTable =
					state.events.tableType === "UNMAPPED_EVENTS" ||
					state.events.tableType === "MAPPED_EVENTS" ||
					state.events.tableType === "SLEPT_EVENTS";
		
			let excludeParkingEvents = false;
			if(marketplace === "TICKET_MASTER" || marketplace === "TICKET_NETWORK")
				excludeParkingEvents = true;

			// 2) Build the params object
			const params: Record<string, any> = {
				tableType: state.events.tableType,
				pageSize: state.events.itemsPerPage,
				page: state.events.currentPage,
				marketplace,
				...(isCompanyFilterTable ? { companyFilterMode } : {}),
				venue: state.events.filters.venue,
				headliner: state.events.filters.eventHeadliner,
				eventName: state.events.filters.eventName,
				startDate: state.events.filters.startDate,
				endDate: state.events.filters.endDate,
				excludeParkingEvents,
				// Include search value if it exists in the URL
				...(searchValue && { searchValue }),
				// Include sort parameters if they exist in the URL
				...(sortByParam && { sortBy: sortByParam }),
				...(sortDirectionParam && { sortDirection: sortDirectionParam }),
				// Include inventoryDisplayMode only if it's explicitly passed
				...(inventoryDisplayMode !== undefined && { inventoryDisplayMode }),
				...(resolvedIncludeInactiveEvents !== undefined && { includeInactiveEvents: resolvedIncludeInactiveEvents }),
				...(isCategoryFilterTable && categoryId.length ? { categoryId: categoryId.join(",") } : {}),
				...(isCompanyFilterTable && companyIds.length ? { companyIds: companyIds.join(",") } : {}),
				...(hasNotes ? { hasNotes: true } : {}),
			};

			// 3) Filter out null/undefined/empty values
			const filteredParams = Object.fromEntries(
				Object.entries(params).filter(([_, value]) => value !== null && value !== undefined && value !== ""),
			);

			// 4) Make the API call
			const response = await axios.get(`${getBackendUrl()}/marketplace-events`, {
				params: filteredParams,
				headers: getAuthHeaders(),
			});

			return { ...response.data, requestId };
		} catch (error) {
			return rejectWithValue("Failed to fetch events: " + String(error));
		}
	},
);

export const sleepEvent = createAsyncThunk(
	"events/sleepEvent",
	async (
		{
			eventId,
			marketplace,
			sleptUntil,
			note,
		}: { eventId: string; marketplace?: string; sleptUntil: string; note?: string },
		{ rejectWithValue },
	) => {
		try {
			const url = `${getBackendUrl()}/events/sleep?marketplace=${marketplace}`;

			const response = await axios.post(
				url,
				{ eventIds: [eventId], sleptUntil, note },
				{ headers: getAuthHeaders() },
			);

			if (!response.data.success) {
				return rejectWithValue(response.data.error || "Failed to sleep event");
			}

			return { eventId, ...response.data };
		} catch (error) {
			const errorMessage = "Failed to sleep event" + error;
			return rejectWithValue(errorMessage);
		}
	},
);

export const submitEventRequest = createAsyncThunk(
	"events/submitEventRequest",
	async (
		{
			eventId,
			marketplace,
			subject,
			primaryEventUrl,
			notes,
			eventName,
			date,
			time,
			venue,
			city,
		}: {
			eventId: string;
			marketplace?: string;
			subject: string;
			primaryEventUrl: string;
			notes?: string;
			eventName?: string;
			date?: string;
			time?: string;
			venue?: string;
			city?: string;
		},
		{ rejectWithValue },
	) => {
		try {
			const url = `${getBackendUrl()}/events/${eventId}/submit-request?marketplace=${marketplace}`;

			const response = await axios.post(
				url,
				{ subject, primaryEventUrl, notes, eventName, date, time, venue, city },
				{ headers: getAuthHeaders() },
			);

			if (!response.data.success) {
				return rejectWithValue(response.data.error || "Failed to submit event request");
			}

			return { eventId, ...response.data };
		} catch (error) {
			return rejectWithValue("Failed to submit event request" + error);
		}
	},
);

export const getEventRequestPrefill = createAsyncThunk(
	"events/getEventRequestPrefill",
	async ({ eventId, marketplace }: { eventId: string; marketplace?: string }, { rejectWithValue }) => {
		try {
			const url = `${getBackendUrl()}/events/${eventId}/request-prefill?marketplace=${marketplace}`;

			const response = await axios.get(url, { headers: getAuthHeaders() });

			return { primaryEventUrl: (response.data?.primaryEventUrl as string) || "" };
		} catch (error) {
			return rejectWithValue("Failed to load event request prefill" + error);
		}
	},
);

export const deleteEvent = createAsyncThunk(
	"events/deleteEvent",
	async ({ eventId, marketplace }: { eventId: string, marketplace?: string }, { rejectWithValue }) => {
		try {
			const url = `${getBackendUrl()}/events?marketplace=${marketplace}`;

			await axios.delete(
				url,
				{
					data: { eventIds: [eventId] },
					headers: getAuthHeaders(),
				},
			);

			return eventId;
		} catch (error) {
			return rejectWithValue("Failed to delete event" + error);
		}
	},
);

export const mapEvent = createAsyncThunk(
	"events/mapEvent",
	async (params: { eventId: string; vividSeatsEventId: string; isSleptEvent: boolean; createAlias?: boolean; createCityAlias?: boolean }, thunkAPI) => {
		const { eventId, vividSeatsEventId, isSleptEvent, createAlias, createCityAlias } = params;
		const { rejectWithValue } = thunkAPI;

		try {
			const requestBody: any = {
				marketplaceEventId: eventId,
				action: "map",
				vividSeatsEventId,
			};
			// Only include isSleptEvent if true
			if (isSleptEvent) {
				requestBody.isSleptEvent = true;
			}
			if (createAlias) {
				requestBody.createAlias = true;
			}
			if (createCityAlias) {
				requestBody.createCityAlias = true;
			}

			const response = await axios.post(`${getBackendUrl()}/events`, requestBody, { headers: getAuthHeaders() });

			if (!response.data.success) {
				return rejectWithValue(response.data.error || "Failed to map event");
			}

			return { eventId, ...response.data };
		} catch (error) {
			const errorMessage = "Failed to map event: " + error;
			return rejectWithValue(errorMessage);
		}
	},
);

export const overrideEvent = createAsyncThunk<
	any,
	{ marketplaceEventId: string; vividSeatsEventId: string; currentVividSeatsEventId: string },
	{ rejectValue: string }
>(
	"events/overrideEvent",
	async ({ marketplaceEventId, vividSeatsEventId, currentVividSeatsEventId }, { rejectWithValue }) => {
		try {
			const response = await axios.post(
				`${getBackendUrl()}/events`,
				{ action: "override", marketplaceEventId, vividSeatsEventId, currentVividSeatsEventId },
				{ headers: getAuthHeaders() },
			);
			if (!response.data?.success) {
				return rejectWithValue(response.data?.error || "Failed to override event mapping");
			}
			return response.data;
		} catch (error) {
			return rejectWithValue("Failed to override event mapping: " + String(error));
		}
	},
);

export const unmapEvent = createAsyncThunk("events/unmapEvent", async (params: { eventId: string; vividSeatsEventId?: string }, thunkAPI) => {
	const { eventId, vividSeatsEventId } = params;
	const { rejectWithValue } = thunkAPI;

	try {
		const response = await axios.post(`${getBackendUrl()}/events`, {
			marketplaceEventId: eventId,
			action: "unmap",
			...(vividSeatsEventId ? { vividSeatsEventId } : {}),
		}, { headers: getAuthHeaders() });

		if (!response.data.success) {
			return rejectWithValue(response.data.error || "Failed to map event");
		}

		return { eventId, ...response.data };
	} catch (error) {
		const errorMessage = "Failed to map event: " + error;
		return rejectWithValue(errorMessage);
	}
});

export const editEvent = createAsyncThunk(
	"events/editEvent",
	async (params: { marketplaceEventId: string; newMarketplaceEventId: string, vividSeatsEventId: string, createAlias?: boolean, createCityAlias?: boolean }, thunkAPI) => {
		const { marketplaceEventId, newMarketplaceEventId, vividSeatsEventId, createAlias, createCityAlias } = params;
		const { rejectWithValue } = thunkAPI;

		try {
			const response = await axios.post(`${getBackendUrl()}/events`, {
				marketplaceEventId: marketplaceEventId,
				action: "edit",
				vividSeatsEventId: vividSeatsEventId,
				newMarketplaceEventId: newMarketplaceEventId,
				createAlias,
				createCityAlias,
			}, { headers: getAuthHeaders() });

			if (!response.data.success) {
				return rejectWithValue(response.data.error || "Failed to edit mapping event");
			}

			return { eventId: marketplaceEventId, ...response.data };
		} catch (error) {
			const errorMessage = "Failed to map event: " + error;
			return rejectWithValue(errorMessage);
		}
	},
);

export const bulkSleepEvents = createAsyncThunk(
	"events/bulkSleepEvents",
	async (
		{
			eventIds,
			marketplace,
			sleptUntil,
			note,
		}: { eventIds: Array<string>; marketplace: string; sleptUntil: string; note?: string },
		{ rejectWithValue },
	) => {
		try {
			const response = await axios.post(
				`${getBackendUrl()}/events/sleep?marketplace=${marketplace}`,
				{ eventIds, sleptUntil, note },
				{ headers: getAuthHeaders() },
			);

			if (!response.data.success) {
				return rejectWithValue(response.data.error || "Failed to sleep events");
			}

			return { eventIds, ...response.data };
		} catch (error) {
			const errorMessage = "Failed to sleep events: " + error;
			return rejectWithValue(errorMessage);
		}
	},
);

export const awakeEvent = createAsyncThunk(
	"events/awakeEvent",
	async ({ eventId, marketplace }: { eventId: string; marketplace?: string }, { rejectWithValue }) => {
		try {
			const url = `${getBackendUrl()}/events/${eventId}?marketplace=${marketplace}`;
			await axios.delete(url, { headers: getAuthHeaders() });
			return { eventId };
		} catch (error) {
			return rejectWithValue("Failed to awake event: " + error);
		}
	},
);

export const bulkAwakeEvents = createAsyncThunk(
	"events/bulkAwakeEvents",
	async ({ eventIds, marketplace }: { eventIds: Array<string>; marketplace: string }, { rejectWithValue }) => {
		try {
			await axios.delete(`${getBackendUrl()}/events?marketplace=${marketplace}`, {
				data: { eventIds },
				headers: getAuthHeaders(),
			});
			return { eventIds };
		} catch (error) {
			return rejectWithValue("Failed to awake events: " + error);
		}
	},
);

export const bulkDeleteEvents = createAsyncThunk(
	"events/bulkDeleteEvents",
	async (eventIds: Array<string>, { rejectWithValue }) => {
		try {
			await axios.delete(`${getBackendUrl()}/events`, { data: { eventIds }, headers: getAuthHeaders() });
			return { eventIds };
		} catch (error) {
			return rejectWithValue("Failed to delete events: " + error);
		}
	},
);

export const bulkMapUnmapEvents = createAsyncThunk(
	"events/bulkMapUnmapEvents",
	async (params: {
		eventMappings: Array<{ eventId: string; vividSeatsEventId: string }>;
		action: "unmap"
	}, { rejectWithValue }) => {
		const { eventMappings, action } = params;

		try {
			const response = await axios.post(`${getBackendUrl()}/events/bulk`, {
				eventMappings,
				action,
			}, { headers: getAuthHeaders() });

			if (!response.data.success) {
				return rejectWithValue(response.data.error || `Failed to ${action} events`);
			}

			return { eventMappings, ...response.data };
		} catch (error) {
			return rejectWithValue(`Failed to ${action} events: ` + error);
		}
	},
);

export const fetchMappingSuggestions = createAsyncThunk<
	// The payload type returned on success
	MappingSuggestionsResponse,
	// The argument we pass when calling fetchMappingSuggestions
	{ eventId: string; marketplace: string, city?: string; dateFrom?: string; dateTo?: string, marketplaceEventId?: string; },
	// The thunkAPI types
	{ rejectValue: string }
>(
	"events/fetchMappingSuggestions",
	async (params, { rejectWithValue }) => {
		try {
			const { eventId, marketplace, city, dateFrom, dateTo, marketplaceEventId } = params;

			let url = `${getBackendUrl()}/mapping-suggestions/${eventId}?marketplace=${marketplace}`;

			if (marketplaceEventId) {
				url += `&marketplaceEventId=${marketplaceEventId}`;
			} else {
				if (city) url += `&city=${city}`;
				if (dateFrom) url += `&startDate=${dateFrom}`;
				if (dateTo) url += `&endDate=${dateTo}`;
			}
			const response = await axios.get(url);
			return response.data; // => { total_events_count, events }
		} catch (error) {
			console.error(error);
			return rejectWithValue("Failed to fetch mapping suggestions: " + String(error));
		}
	},
);


export const searchSFCatalog = createAsyncThunk(
	"events/searchSFCatalog",
	async (
		params: { name: string | null; eventId: string; marketplace: string; startDate?: string; endDate?: string; city?: string },
		{ rejectWithValue },
	) => {
		const { name, eventId, marketplace, startDate, endDate, city } = params;

		try {
			let url = `${getBackendUrl()}/mapping-suggestions/${eventId}?marketplace=${marketplace}`;
			if (startDate) {
				url += `&startDate=${startDate}`;
			}
			if (endDate) {
				url += `&endDate=${endDate}`;
			}
			if (city) {
				url += `&city=${city}`;
			}
			const pageSize = name ? 2000 : 100;
			url += `&page=1&pageSize=${pageSize}`;
			const response = await axios.get(url, {
				params: {
					name,
				},
				headers: getAuthHeaders(),
			});

			return response.data;
		} catch (error) {
			return rejectWithValue("Failed to search SF catalog: " + error);
		}
	},
);

export const eventsSlice = createSlice({
	name: "events",
	initialState,
	reducers: {
		setFilters: (state, action) => {
			state.filters = action.payload;
		},
		setTableType: (state, action) => {
			state.tableType = action.payload;
			state.events = [];
			state.totalEventsCount = 0;
			state.loading = "pending";
		},
		setCurrentPage: (state, action) => {
			state.currentPage = action.payload;
		},
		setItemsPerPage: (state, action) => {
			state.itemsPerPage = action.payload;
			state.currentPage = 1;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchEvents.pending, (state, action) => {
				state.loading = "pending";
				state.error = null;
				state.latestRequestId = action.meta.arg.requestId; // Track latest requestId
			})
			.addCase(fetchEvents.fulfilled, (state, action) => {
				// Only apply data if requestId matches the latestRequestId in state
				if (action.payload.requestId !== state.latestRequestId) return;

				state.loading = "succeeded";
				state.events = action.payload.events;
				state.totalEventsCount = action.payload.total_events_count;
			})
			.addCase(fetchEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
				state.events = [];
				state.totalEventsCount = 0;
			})
			.addCase(editEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(editEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload.eventId);
			})
			.addCase(editEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(sleepEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(sleepEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload.eventId);
			})
			.addCase(sleepEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(deleteEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(deleteEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload);
			})
			.addCase(deleteEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(mapEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(mapEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload.eventId);
			})
			.addCase(mapEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(unmapEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(unmapEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload.eventId);
			})
			.addCase(unmapEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(bulkSleepEvents.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(bulkSleepEvents.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => !action.payload.eventIds.includes(event.id));
			})
			.addCase(bulkSleepEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(awakeEvent.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(awakeEvent.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => event.id !== action.payload.eventId);
			})
			.addCase(awakeEvent.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(bulkAwakeEvents.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(bulkAwakeEvents.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => !action.payload.eventIds.includes(event.id));
			})
			.addCase(bulkAwakeEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(bulkDeleteEvents.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(bulkDeleteEvents.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => !action.payload.eventIds.includes(event.id));
			})
			.addCase(bulkDeleteEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(bulkMapUnmapEvents.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(bulkMapUnmapEvents.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.events = state.events.filter((event) => !action.payload.eventMappings.map((mapping: any) => mapping.eventId).includes(event.id));
			})
			.addCase(bulkMapUnmapEvents.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(searchSFCatalog.pending, (state) => {
				state.loading = "succeeded";
			})
			.addCase(searchSFCatalog.fulfilled, (state) => {
				state.loading = "succeeded";
			})
			.addCase(searchSFCatalog.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			})
			.addCase(fetchMappingSuggestions.pending, (state) => {
				state.loadingSuggestions = "pending";
				state.error = null;
			})
			.addCase(fetchMappingSuggestions.fulfilled, (state, action) => {
				state.loadingSuggestions = "succeeded";
				// action.payload has { total_events_count, events }
				state.mappingSuggestions = action.payload.events;
			})
			.addCase(fetchMappingSuggestions.rejected, (state, action) => {
				state.loadingSuggestions = "failed";
				state.error = action.payload as string;
			});
	},
});

export default eventsSlice.reducer;
